import React, { ComponentType } from 'react';
import { Heading04, BodyCopy } from '@uds/typography';
import PaddingBox from '@uds/padding-box';
import Illustration from '@uds/illustrations';
import { useBreakpoints } from '@ux/responsive';
import { useTheme, Colorway } from '@uds/theme-utils';
import { Size } from '@uds/button';
import { CTA } from 'core/ribbons/cta';
import { ImagesWithAspectRatios } from 'core/ribbons/primitives';
import createImageAlt from 'presentation/utils/createImageAlt';
import urlLanguageParse from 'presentation/utils/urlLanguageParse';
import withLanguage from 'presentation/hocs/withLanguage';
import { isNilOrEmpty } from 'presentation/utils/generalUtils';
import getActiveImageWithAspectRatios from 'presentation/utils/getActiveImageWithAspectRatios';
import Shortcode, { ShortcodeProps } from 'components/Shortcode';
import CallToAction from 'presentation/components/common/CallToAction';

import useStylesheet, { getClassNames } from './Pillar.stylesheet';

export interface OuterProps {
  hBaseline: number;
  illustration?: string;
  imageUrl?: string;
  imagesWithAspectRatios?: ImagesWithAspectRatios;
  title?: ShortcodeProps;
  body?: ShortcodeProps;
  colorway: Colorway;
  ctas?: Array<CTA>;
  ribbonId?: string;
}
export interface InnerProps extends OuterProps {
  currentLanguage: string;
}

export const PurePillar = ({
  hBaseline,
  illustration,
  imageUrl,
  imagesWithAspectRatios,
  title,
  body,
  colorway,
  currentLanguage,
  ctas,
  ribbonId,
}: InnerProps) => {
  const { theme } = useTheme();
  const breakpoints = useBreakpoints();
  const { isMinSm } = breakpoints;
  const { styles } = useStylesheet({
    theme,
    colorway,
    ctas,
  });
  const activeImage = getActiveImageWithAspectRatios(
    breakpoints,
    {
      imageUrl,
    },
    imagesWithAspectRatios,
  );
  const showImage = (url: string | undefined): url is string => {
    return isNilOrEmpty(illustration) && !isNilOrEmpty(url);
  };
  const showIllustration = (illustration: string | undefined): illustration is string => !isNilOrEmpty(illustration);
  const hasTitle = !isNilOrEmpty(title);
  const hasBody = !isNilOrEmpty(body);

  const classNames = getClassNames();
  return (
    <PaddingBox className={classNames.root}>
      <div>
        {showIllustration(illustration) ? (
          <Illustration size={styles.illustration.width as number} icon={illustration} />
        ) : null}
        {showImage(activeImage.url) ? (
          <div style={styles.imageContainer}>
            <img
              loading="lazy"
              src={urlLanguageParse(activeImage.url, currentLanguage)}
              alt={createImageAlt({ imageUrl: activeImage.url })}
              style={styles.image}
            />
          </div>
        ) : null}
      </div>
      <div style={styles.content}>
        {hasTitle && (
          <Heading04 depth={hBaseline}>
            <Shortcode code={title} against={colorway} />
          </Heading04>
        )}
        {hasBody && (
          <BodyCopy>
            <Shortcode code={body} against={colorway} />
          </BodyCopy>
        )}
        {ctas == null ? null : (
          <div style={styles.ctas}>
            {ctas.map((cta, i) => (
              <div style={styles.cta} key={(ribbonId ?? '').concat(String(i))}>
                <CallToAction block={!isMinSm} ribbonId={ribbonId} size={Size.SM} index={i} {...cta} />
              </div>
            ))}
          </div>
        )}
      </div>
    </PaddingBox>
  );
};

PurePillar.displayName = 'Pillar';

export default withLanguage(PurePillar) as ComponentType<OuterProps>;
